// WAAP Analytics → Monitoring — NATIVE charts (no Superset iframe). The BE
// pre-normalizes Superset dashboard 69 ("scdn.v2_Monitoring") into typed widgets
// (number / pie / table / timeseries), same pipeline as the Dashboard overview.
//   GET /analytics/monitoring/charts?tz=…&range=…&domains=…
import { useQuery } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { waapKeys, waapPath, WAAP_AREAS } from './_shared';
import type { WaapChartsResponse } from './dashboard-charts';

/** Time-range options for the Monitoring tab (mirrors the vendor's selector). */
export const MONITORING_TIME_RANGES = [
  'Last Hour',
  'Last 4 Hours',
  'Last 12 Hours',
  'Last 24 Hours',
] as const;
export type MonitoringTimeRange = (typeof MONITORING_TIME_RANGES)[number];

/** Read the normalized Monitoring widgets for a website's account. `domains` is
 *  the optional hostname filter (empty = all). Returns null when the board is
 *  not configured server-side (400/404/501). */
export function useWaapMonitoringCharts(
  domainId: string | undefined,
  range = 'Last 12 Hours',
  domains: string[] = [],
) {
  const domainsKey = [...domains].sort().join(',');
  return useQuery({
    queryKey: [...waapKeys.aspect(domainId ?? '', WAAP_AREAS.monitoringCharts), range, domainsKey] as const,
    enabled: !!domainId,
    retry: false,
    staleTime: 60_000,
    queryFn: async (): Promise<WaapChartsResponse | null> => {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const params = new URLSearchParams({ tz, range });
        if (domainsKey) params.set('domains', domainsKey);
        const path = waapPath(domainId as string, WAAP_AREAS.monitoringCharts);
        const res = await serviceApiV3.get<WaapChartsResponse>(`${path}?${params.toString()}`);
        return res.data;
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404 || status === 501) return null;
        throw e;
      }
    },
  });
}
